// Земля карты мира v8 на WebGL2: рельеф (карта высот), цвет из рендера Blender (обзорная текстура + подробные
// плитки у камеры), освещение по времени суток (солнце/луна, тени через shadow map), вода с глубиной, бликами
// и прибоем, деревья-«billboard» с картами нормалей, каменные мосты. Объекты игры (замки, войска, подписи) рисует
// 2D-слой поверх — с той же камерой (camera3d.ts).
import { CELL, type Daylight, MAP_BRIDGES, archipelagoGrid } from '@sk/shared';
import { type View3D, invert4, mul4 } from './camera3d';
import { V8_VERSION } from './v8version';

const BASE = '/art/world/v8';
/** Версия в адресах: иначе кэш сервис-воркера смешивает файлы разных выкладок. */
const VQ = `?v=${V8_VERSION}`;
const CHUNK = 32; // клеток в стороне участка рельефа
const SHADOW = 2048;
/** Мост шире модели Codex (настил 1.4 → ≈2.1 клетки): отряд помещается. */
const BRIDGE_WIDEN = 1.5;
const BRIDGE_HALF = 0.7 * BRIDGE_WIDEN;

interface Manifest {
  size: number;
  rows: number;
  ppc: number;
  tile: number;
  tiles: [number, number][];
  heightW: number;
  heightH: number;
  treeVariants: string[];
  trees: number;
}
interface AtlasItem {
  x: number;
  y: number;
  w: number;
  h: number;
  anchorX: number;
  anchorY: number;
  heightUnits: number;
  widthUnits: number;
}
interface Atlas {
  camera_elevation_deg: number;
  items: Record<string, AtlasItem>;
}
interface BridgePiece {
  positions: number[];
  normals: number[];
  uvs: number[];
  indices: number[];
  length?: number;
}

/** Картинка на земле внутри 3D-сцены (отряд в лесу): деревья перед ней её закрывают. */
export interface GroundSprite {
  key: string;
  x: number;
  y: number;
  canvas: HTMLCanvasElement;
  /** Размер картинки в клетках и точка опоры (доли от левого верхнего угла). */
  w: number;
  h: number;
  ax: number;
  ay: number;
}
/**
 * Постройка (замок, башня, шахта…) внутри 3D-сцены: щит с картинкой стоит на земле, пишет глубину —
 * деревья перед ним закрывают его низ, а он закрывает лес и постройки за собой; отбрасывает тень от солнца.
 */
export interface GroundBuilding {
  key: string;
  img: HTMLImageElement;
  x: number;
  y: number;
  /** Размер картинки в клетках и точка опоры (доли от левого верхнего угла). */
  w: number;
  h: number;
  ax: number;
  ay: number;
  /** Серость (руины) и затемнение (0…1). */
  gray?: number;
  dim?: number;
}
/** 3D-модель постройки (замок): url папки модели, место (клетки), ширина в клетках, поворот (радианы). */
export interface GroundModel {
  url: string;
  x: number;
  y: number;
  w: number;
  rot: number;
}
/** Купол щита в 3D: центр на земле, радиус и высота — в клетках. */
export interface GroundShield {
  x: number;
  y: number;
  r: number;
  h: number;
}
export interface GroundRenderOptions {
  buildings?: GroundBuilding[];
  models?: GroundModel[];
  shields?: GroundShield[];
  shadows?: boolean;
  /** Прозрачность ромбической сетки клеток (0 — нет). */
  grid?: number;
  sprites?: GroundSprite[];
  /** Живая 3D-трава у камеры (настройка «эффекты»). */
  grass?: boolean;
}

export interface WorldGround {
  /** Высота земли (клетки) — для 2D-слоя и нажатий. Над мостом — высота настила. */
  heightAt(x: number, y: number): number;
  /** Лес у построек вырублен: деревья в этих клетках не рисуются. */
  setClearedCells(cells: Uint8Array | null): void;
  render(view: View3D, light: Daylight, timeSec: number, opts?: GroundRenderOptions): void;
  resize(w: number, h: number, dpr: number): void;
  /** Догрузка подробных плиток вокруг видимой области. */
  readonly ready: boolean;
  destroy(): void;
}

// ───────────────────────── шейдеры ─────────────────────────
const COMMON = /* glsl */ `#version 300 es
precision highp float;
precision highp sampler2DShadow;
uniform mat4 uVP;
uniform mat4 uSunVP;
uniform sampler2D uHeight;
uniform vec2 uHSize;
uniform vec2 uWorld;
uniform vec3 uSunDir;
uniform vec3 uSunCol;
uniform vec3 uSkyCol;
uniform vec3 uEye;
uniform vec4 uFog;   // начало, конец, сила, —
uniform vec3 uFogCol;
uniform float uTime;
float H(vec2 cell) { return texture(uHeight, (cell * 2.0 + 0.5) / uHSize).r; }
`;

const SHADOW_FN = /* glsl */ `
uniform sampler2DShadow uShadow;
uniform float uShadowOn;
float shadowAt(vec4 lp) {
  if (uShadowOn < 0.5) return 1.0;
  vec3 p = lp.xyz / lp.w * 0.5 + 0.5;
  if (p.x <= 0.0 || p.x >= 1.0 || p.y <= 0.0 || p.y >= 1.0 || p.z >= 1.0) return 1.0;
  vec2 t = 1.0 / vec2(textureSize(uShadow, 0));
  float s = 0.0;
  for (int i = -1; i <= 1; i++)
    for (int j = -1; j <= 1; j++) s += texture(uShadow, vec3(p.xy + vec2(i, j) * t * 1.2, p.z - 0.0012));
  return s / 9.0;
}
vec3 fogged(vec3 col, vec3 pos) {
  float d = length(pos - uEye);
  return mix(col, uFogCol, smoothstep(uFog.x, uFog.y, d) * uFog.z);
}
vec3 toSrgb(vec3 c) { return pow(clamp(c, 0.0, 1.0), vec3(1.0 / 2.2)); }
`;

const GRID_FN = /* glsl */ `
uniform float uGrid;
// Сетка под 45° (как стоят здания): ромб размером с лагерь разбойников на карте (≈1,5 клетки).
const float GRID_CELLS = 1.5;
float gridLine(vec2 cell) {
  vec2 g = (vec2(cell.x + cell.y, cell.x - cell.y) - 0.5) / GRID_CELLS;
  vec2 d = abs(fract(g + 0.5) - 0.5);
  vec2 w = max(fwidth(g), vec2(1e-4));
  vec2 l = 1.0 - smoothstep(w * 0.35, w * 1.1, d);
  return max(l.x, l.y);
}
`;

const TERRAIN_VS =
  COMMON +
  /* glsl */ `
in vec2 aPos;
uniform vec2 uChunk;
out vec2 vCell;
out vec3 vPos;
out vec4 vLight;
void main() {
  vec2 cell = uChunk + aPos * 0.5;
  ivec2 tc = ivec2(clamp(cell * 2.0, vec2(0.0), uHSize - 1.0));
  float z = texelFetch(uHeight, tc, 0).r;
  vec3 p = vec3(cell.x, -cell.y, z);
  vCell = cell;
  vPos = p;
  vLight = uSunVP * vec4(p, 1.0);
  gl_Position = uVP * vec4(p, 1.0);
}`;

const TERRAIN_FS =
  COMMON +
  SHADOW_FN +
  GRID_FN +
  /* glsl */ `
uniform sampler2D uOverview;
uniform sampler2D uDetail;
uniform vec4 uDetailRect; // x0, y0, размер (клетки), есть ли
uniform sampler2D uCleared; // R: вырубленный у построек лес (1) — тёмный подлесок становится травой
in vec2 vCell;
in vec3 vPos;
in vec4 vLight;
out vec4 outColor;
float thash(vec2 p) { return fract(sin(dot(p, vec2(41.3, 289.1))) * 15731.743); }
float aaFade(vec2 p) {
  vec2 fw = fwidth(p);
  return 1.0 - smoothstep(0.3, 0.8, max(fw.x, fw.y));
}
float tnoise(vec2 p) {
  vec2 i = floor(p), f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(thash(i), thash(i + vec2(1, 0)), u.x), mix(thash(i + vec2(0, 1)), thash(i + vec2(1, 1)), u.x), u.y);
}
void main() {
  vec3 alb = texture(uOverview, vCell / uWorld).rgb;
  if (uDetailRect.w > 0.5) {
    vec2 t = (vCell - uDetailRect.xy) / uDetailRect.z;
    alb = texture(uDetail, clamp(t, vec2(0.0), vec2(1.0))).rgb;
  }
  alb = pow(alb, vec3(2.2));
  // Вырубка: подлесок без деревьев выглядел тёмным пятном — там луг с пятнами оттенков
  float clr = texture(uCleared, vCell / uWorld).r;
  if (clr > 0.01) {
    float pn = tnoise(vCell * 1.9) * 0.6 + tnoise(vCell * 5.3) * 0.4;
    vec3 meadow = mix(vec3(0.17, 0.29, 0.05), vec3(0.32, 0.40, 0.09), pn);
    alb = mix(alb, meadow, smoothstep(0.15, 0.85, clr));
  }
  // Трава чуть сочнее (зелёные пиксели рендера)
  float grass = smoothstep(0.004, 0.05, alb.g - max(alb.r, alb.b)) * step(0.08, vPos.z);
  alb *= mix(vec3(1.0), vec3(0.90, 0.94, 0.84), grass);
  // Мелкая детализация вблизи: на траве — пятна оттенков, кочки и травинки; на остальной суше — зерно
  float near = 1.0 - smoothstep(30.0, 120.0, length(vPos - uEye));
  // Без ряби: мелкий шум гасится там, где на пиксель приходится больше полупериода (муар на мониторе).
  // Производные считаем до ветвлений — внутри if они не определены.
  // Без вытянутого шума: «травинки» одного направления складывались в борозды, как после комбайна
  vec2 pb1 = vCell * 97.0 + vec2(17.3, 5.1), pb2 = vCell * 173.0 + vec2(3.7, 11.9);
  float fb1 = aaFade(pb1), fb2 = aaFade(pb2), f27 = aaFade(vCell * 27.0), f61 = aaFade(vCell * 61.0);
  if (near > 0.0 && vPos.z > 0.08) {
    float d = tnoise(vCell * 9.0) * 0.55 + mix(0.5, tnoise(vCell * 27.0), f27) * 0.3 + mix(0.5, tnoise(vCell * 61.0), f61) * 0.15;
    alb *= 1.0 + (d - 0.5) * 0.42 * near;
    if (grass > 0.0) {
      float patchy = tnoise(vCell * 1.7) * 0.6 + tnoise(vCell * 4.3) * 0.4;
      vec3 tint = mix(vec3(0.82, 0.92, 0.62), vec3(1.10, 1.04, 0.78), patchy);
      // мелкая рябь травы (без направления) + редкие светлые кончики
      float blades = mix(0.5, tnoise(pb1), fb1) * 0.6 + mix(0.5, tnoise(pb2), fb2) * 0.4;
      float tips = smoothstep(0.78, 0.97, blades) * 0.6;
      float clump = smoothstep(0.35, 0.75, tnoise(vCell * 13.0));
      vec3 g = tint * (0.78 + 0.34 * blades) * (0.9 + 0.2 * clump) + vec3(0.05, 0.07, 0.0) * tips;
      alb = mix(alb, alb * g, grass * near);
    }
  }
  float hl = H(vCell - vec2(0.5, 0.0));
  float hr = H(vCell + vec2(0.5, 0.0));
  float hu = H(vCell - vec2(0.0, 0.5));
  float hd = H(vCell + vec2(0.0, 0.5));
  vec3 n = normalize(vec3(-(hr - hl), hd - hu, 1.0));
  // Стены обрывов сверху не видны в рендере: на крутых склонах — слоистый камень (по высоте и сторонам)
  float steep = smoothstep(0.5, 0.82, 1.0 - n.z);
  if (steep > 0.0) {
    vec2 side = abs(n.x) > abs(n.y) ? vec2(vPos.y, vPos.z) : vec2(vPos.x, vPos.z);
    float strata = 0.5 + 0.5 * sin(vPos.z * 9.0 + tnoise(side * vec2(1.5, 3.0)) * 3.0);
    float grain = tnoise(side * 7.0);
    vec3 rock = mix(vec3(0.30, 0.25, 0.20), vec3(0.55, 0.49, 0.40), 0.35 + 0.4 * strata * grain);
    alb = mix(alb, rock, steep * 0.85);
  }
  float lam = max(dot(n, uSunDir), 0.0);
  float sh = shadowAt(vLight);
  vec3 col = alb * (uSkyCol * (0.55 + 0.45 * n.z) + uSunCol * lam * sh);
  col = mix(col, uSkyCol * 1.6 + uSunCol * 0.5, gridLine(vCell) * uGrid * 0.075);
  outColor = vec4(toSrgb(fogged(col, vPos)), 1.0);
}`;

const DEPTH_VS =
  COMMON +
  /* glsl */ `
in vec2 aPos;
uniform vec2 uChunk;
void main() {
  vec2 cell = uChunk + aPos * 0.5;
  ivec2 tc = ivec2(clamp(cell * 2.0, vec2(0.0), uHSize - 1.0));
  float z = texelFetch(uHeight, tc, 0).r;
  gl_Position = uSunVP * vec4(cell.x, -cell.y, max(z, -0.05), 1.0);
}`;
const DEPTH_FS = /* glsl */ `#version 300 es
precision mediump float;
out vec4 o;
void main() { o = vec4(1.0); }`;

const WATER_VS =
  COMMON +
  /* glsl */ `
in vec2 aPos;
uniform vec4 uRect; // x0, y0, x1, y1 клетки
out vec2 vCell;
out vec3 vPos;
out vec4 vLight;
void main() {
  vec2 cell = mix(uRect.xy, uRect.zw, aPos);
  vec3 p = vec3(cell.x, -cell.y, 0.0);
  vCell = cell;
  vPos = p;
  vLight = uSunVP * vec4(p, 1.0);
  gl_Position = uVP * vec4(p, 1.0);
}`;

const WATER_FS =
  COMMON +
  SHADOW_FN +
  GRID_FN +
  /* glsl */ `
in vec2 vCell;
in vec3 vPos;
in vec4 vLight;
out vec4 outColor;
float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float vnoise(vec2 p) {
  vec2 i = floor(p), f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i), hash(i + vec2(1, 0)), u.x), mix(hash(i + vec2(0, 1)), hash(i + vec2(1, 1)), u.x), u.y);
}
float waves(vec2 p) {
  float t = uTime;
  return vnoise(p * 0.9 + vec2(t * 0.25, t * 0.12)) * 0.5 + vnoise(p * 2.3 - vec2(t * 0.31, -t * 0.22)) * 0.3 +
         vnoise(p * 5.1 + vec2(-t * 0.5, t * 0.4)) * 0.2;
}
void main() {
  float h = H(vCell);
  float depth = -h;
  if (depth < -0.02) discard;
  float e = 0.08;
  float w0 = waves(vCell);
  vec2 g = vec2(waves(vCell + vec2(e, 0.0)) - w0, waves(vCell + vec2(0.0, e)) - w0) / e;
  vec3 n = normalize(vec3(-g.x * 0.16, g.y * 0.16, 1.0));
  vec3 V = normalize(uEye - vPos);
  vec3 Hh = normalize(V + uSunDir);
  float sh = shadowAt(vLight);
  float spec = pow(max(dot(n, Hh), 0.0), 140.0) * sh * step(0.0, uSunDir.z);
  float fres = 0.03 + 0.6 * pow(1.0 - max(dot(n, V), 0.0), 4.0);
  vec3 shallow = vec3(0.03, 0.36, 0.44);
  vec3 deep = vec3(0.012, 0.09, 0.22);
  float dk = smoothstep(0.0, 2.2, depth);
  vec3 light = uSkyCol * 1.5 + uSunCol * (0.35 + 0.25 * sh);
  vec3 col = mix(shallow, deep, dk) * light;
  col += uSkyCol * fres * 0.9 + uSunCol * spec * 2.5;
  // Прибой: узкая полоса пены у самой кромки + редкие бегущие к берегу гребни
  float edge = smoothstep(0.09, 0.0, depth);
  float band = 0.5 + 0.5 * sin(uTime * 1.3 - depth * 30.0 + vnoise(vCell * 0.9) * 5.0);
  float crest = smoothstep(0.2, 0.04, depth) * smoothstep(0.82, 0.98, band);
  float foam = (edge + crest * 0.8) * (0.6 + 0.4 * vnoise(vCell * 5.0 + uTime * 0.25));
  col += foam * (uSkyCol + uSunCol * 0.6) * 0.7;
  // Мелководье тоже читается как вода (при 0,35 песок под ним выглядел пляжем — корабли «на песке»)
  float a = mix(0.62, 0.95, smoothstep(0.0, 0.8, depth));
  a = max(a, clamp(foam, 0.0, 1.0) * 0.85);
  col = mix(col, uSkyCol * 1.6 + uSunCol * 0.5, gridLine(vCell) * uGrid * 0.045);
  outColor = vec4(toSrgb(fogged(col, vPos)), a);
}`;

const TREE_VS =
  COMMON +
  /* glsl */ `
in vec2 aCorner;
in vec4 aInst;   // x, y, z, масштаб
in float aVar;
uniform vec4 uRect[16];
uniform vec4 uDim[16]; // ширина, высота (клетки), anchorX, anchorY
uniform vec3 uRight;
uniform vec3 uUp;
uniform float uShadowPass;
uniform vec3 uSunRight;
uniform vec4 uClear[8]; // x, y, радиус: деревья вплотную к отряду в лесу расступаются
uniform float uPropStart; // виды с этого номера — камни, трава, брёвна: не расступаются
out vec2 vUV;
out vec4 vLight;
out vec3 vPos;
void main() {
  for (int k = 0; k < 8; k++) {
    if (aVar < uPropStart - 0.5 && uClear[k].z > 0.0 && distance(aInst.xy, uClear[k].xy) < uClear[k].z) {
      gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
      return;
    }
  }
  int v = int(aVar + 0.5);
  vec4 r = uRect[v];
  vec4 dm = uDim[v];
  vec3 base = vec3(aInst.x, -aInst.y, aInst.z);
  float w = dm.x * aInst.w;
  float hh = dm.y * aInst.w;
  vec3 right = uShadowPass > 0.5 ? uSunRight : uRight;
  vec3 up = uShadowPass > 0.5 ? vec3(0.0, 0.0, 1.0) : uUp;
  vec3 p = base + right * ((aCorner.x - dm.z) * w) + up * ((dm.w - aCorner.y) * hh);
  vUV = r.xy + aCorner * r.zw;
  vPos = p;
  vLight = uSunVP * vec4(base + vec3(0.0, 0.0, hh * 0.35), 1.0);
  gl_Position = (uShadowPass > 0.5 ? uSunVP : uVP) * vec4(p, 1.0);
}`;

const TREE_FS =
  COMMON +
  SHADOW_FN +
  /* glsl */ `
uniform sampler2D uAlb;
uniform sampler2D uNrm;
uniform vec3 uSprUp;
uniform vec3 uSprToCam;
uniform float uShadowPass;
in vec2 vUV;
in vec4 vLight;
in vec3 vPos;
out vec4 outColor;
void main() {
  vec4 a = texture(uAlb, vUV);
  if (a.a < 0.5) discard;
  if (uShadowPass > 0.5) { outColor = vec4(1.0); return; }
  vec3 nm = texture(uNrm, vUV).xyz * 2.0 - 1.0;
  vec3 nw = normalize(nm.x * vec3(1.0, 0.0, 0.0) + nm.y * uSprUp + nm.z * uSprToCam);
  float lam = max(dot(nw, uSunDir), 0.0);
  float sh = shadowAt(vLight);
  vec3 col = pow(a.rgb, vec3(2.2)) * (uSkyCol * 1.05 + uSunCol * lam * (0.35 + 0.65 * sh));
  outColor = vec4(toSrgb(fogged(col, vPos)), 1.0);
}`;

// Живая трава у камеры: травинки (полоса из 5 вершин) на повторяющейся плитке вокруг центра экрана.
// Растёт только там, где рисунок карты зелёный (дороги, вода, песок, скалы — без травы).
const GRASS_VS =
  COMMON +
  /* glsl */ `
in vec2 aCorner;  // x: -1..1 поперёк, y: 0..1 вдоль травинки
in vec4 aBlade;   // позиция на плитке (клетки), угол 0..1, рост 0..1
uniform sampler2D uOverview;
uniform vec2 uCenter;
uniform float uTile;
uniform float uRadius;
uniform float uPx;   // клеток на пиксель экрана
out vec3 vCol;
out vec3 vPos;
out vec4 vLight;
void main() {
  vec2 o = uCenter - 0.5 * uTile;
  vec2 p = o + mod(aBlade.xy - o, uTile);
  float fade = 1.0 - smoothstep(0.65, 1.0, length(p - uCenter) / uRadius);
  vec3 alb = texture(uOverview, p / uWorld).rgb;
  float z = H(p);
  float g = smoothstep(0.012, 0.06, alb.g - max(alb.r, alb.b)) * step(0.1, z);
  float s = g * fade;
  if (s < 0.08) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }
  float t = aCorner.y;
  float h = (0.09 + 0.12 * aBlade.w) * (0.4 + 0.6 * s);
  float a = aBlade.z * 6.2832;
  vec2 dir = vec2(cos(a), sin(a));
  float w = max(0.018, 1.2 * uPx) * (1.0 - t * 0.85);
  // наклон и ветер — сильнее к кончику
  float wind = sin(uTime * 1.6 + p.x * 0.9 + p.y * 0.55) * 0.5 + sin(uTime * 2.7 + p.y * 1.7) * 0.25;
  vec2 bend = (vec2(dir.y, -dir.x) * (aBlade.w - 0.5) * 0.06 + vec2(0.035, 0.012) * wind) * t * t;
  vec2 xy = p + dir * aCorner.x * w + bend;
  vec3 pos = vec3(xy.x, -xy.y, z + h * t);
  vec3 base = pow(alb, vec3(2.2)) * vec3(0.74, 0.80, 0.70);
  vCol = mix(base * 0.55, base * vec3(1.25, 1.3, 1.0) + vec3(0.02, 0.025, 0.0), t);
  vPos = pos;
  vLight = uSunVP * vec4(pos, 1.0);
  gl_Position = uVP * vec4(pos, 1.0);
}`;

const GRASS_FS =
  COMMON +
  SHADOW_FN +
  /* glsl */ `
in vec3 vCol;
in vec3 vPos;
in vec4 vLight;
out vec4 outColor;
void main() {
  float sh = shadowAt(vLight);
  vec3 col = vCol * (uSkyCol * 0.9 + uSunCol * max(uSunDir.z, 0.0) * (0.3 + 0.7 * sh));
  outColor = vec4(toSrgb(fogged(col, vPos)), 1.0);
}`;

const MESH_VS =
  COMMON +
  /* glsl */ `
in vec3 aP;
in vec3 aN;
in vec2 aUV;
uniform mat4 uModel;
uniform float uShadowPass;
out vec3 vN;
out vec2 vUV;
out vec4 vLight;
out vec3 vPos;
void main() {
  vec4 p = uModel * vec4(aP, 1.0);
  vN = normalize(mat3(uModel) * aN);
  vUV = vec2(aUV.x, 1.0 - aUV.y); // UV Blender — снизу вверх
  vPos = p.xyz;
  vLight = uSunVP * p;
  gl_Position = (uShadowPass > 0.5 ? uSunVP : uVP) * p;
}`;

const MESH_FS =
  COMMON +
  SHADOW_FN +
  /* glsl */ `
uniform sampler2D uTex;
uniform float uShadowPass;
in vec3 vN;
in vec2 vUV;
in vec4 vLight;
in vec3 vPos;
out vec4 outColor;
void main() {
  if (uShadowPass > 0.5) { outColor = vec4(1.0); return; }
  vec3 n = normalize(vN);
  vec3 alb = pow(texture(uTex, vUV).rgb, vec3(2.2));
  float lam = max(dot(n, uSunDir), 0.0);
  vec3 col = alb * (uSkyCol * (0.6 + 0.4 * max(n.z, 0.0)) + uSunCol * lam * shadowAt(vLight));
  outColor = vec4(toSrgb(fogged(col, vPos)), 1.0);
}`;

const SPRITE_VS =
  COMMON +
  /* glsl */ `
in vec2 aCorner;
uniform vec3 uBase;
uniform vec4 uRect; // ширина, высота (клетки), опора x, y
uniform vec3 uRight;
uniform vec3 uUp;
out vec2 vUV;
void main() {
  vec3 p = uBase + uRight * ((aCorner.x - uRect.z) * uRect.x) + uUp * ((uRect.w - aCorner.y) * uRect.y);
  vUV = aCorner;
  gl_Position = uVP * vec4(p, 1.0);
}`;
const SPRITE_FS = /* glsl */ `#version 300 es
precision highp float;
uniform sampler2D uTex;
uniform vec3 uTint;
in vec2 vUV;
out vec4 outColor;
void main() {
  vec4 c = texture(uTex, vUV);
  if (c.a < 0.03) discard;
  outColor = vec4(c.rgb * uTint, c.a);
}`;

// Постройки: щит с картинкой, глубина и тень (как у деревьев)
const BUILD_VS =
  COMMON +
  /* glsl */ `
in vec2 aCorner;
uniform vec3 uBase;
uniform vec4 uRect; // ширина, высота (клетки), опора x, y
uniform vec3 uRight;
uniform vec3 uUp;
uniform float uShadowPass; // 0 — картинка, 2 — её тень на земле
uniform vec3 uShadowDir;   // от солнца по земле, длина = 1 / tg(высоты солнца)
out vec2 vUV;
void main() {
  vec3 p;
  if (uShadowPass > 1.5) {
    // силуэт лёг на землю: высота картинки → длина тени вдоль uShadowDir
    p = uBase + uRight * ((aCorner.x - uRect.z) * uRect.x) + uShadowDir * ((uRect.w - aCorner.y) * uRect.y);
    p.z = uBase.z + 0.03;
  } else {
    p = uBase + uRight * ((aCorner.x - uRect.z) * uRect.x) + uUp * ((uRect.w - aCorner.y) * uRect.y);
  }
  vUV = aCorner;
  gl_Position = uVP * vec4(p, 1.0);
}`;
const BUILD_FS = /* glsl */ `#version 300 es
precision highp float;
uniform sampler2D uTex;
uniform vec3 uTint;
uniform float uGray;
uniform float uShadowPass;
in vec2 vUV;
out vec4 outColor;
void main() {
  vec4 c = texture(uTex, vUV);
  if (uShadowPass > 1.5) {
    // тень: полупрозрачный тёмный силуэт (плотность — в uTint.x)
    if (c.a < 0.3) discard;
    outColor = vec4(0.02, 0.03, 0.05, uTint.x);
    return;
  }
  if (c.a < 0.45) discard;
  float g = dot(c.rgb, vec3(0.3, 0.59, 0.11));
  outColor = vec4(mix(c.rgb, vec3(g), uGray) * uTint, 1.0);
}`;

// Щит: светящаяся рамка-ромб на земле (скруглённый квадрат под 45°, суперэллипс) с бегущим бликом
const RING_VS =
  COMMON +
  /* glsl */ `
in vec2 aCorner;
uniform vec3 uCenter;
uniform float uSize;
out vec2 vL;
void main() {
  vL = (aCorner * 2.0 - 1.0) * uSize * 1.3;
  // рамка лежит по рельефу (на склоне не уходит в землю)
  vec2 w = uCenter.xy + vL;
  gl_Position = uVP * vec4(w, H(vec2(w.x, -w.y)) + 0.06, 1.0);
}`;
const RING_FS =
  COMMON +
  /* glsl */ `
in vec2 vL;
uniform float uSize;
uniform vec3 uSide; // xy — к камере по земле, z — какая половина (+1 передняя, -1 задняя)
out vec4 outColor;
void main() {
  if (dot(vL, uSide.xy) * uSide.z < 0.0) discard;
  // оси ромба (как сетка карты под 45°)
  vec2 q = vec2(vL.x + vL.y, vL.x - vL.y) * 0.70710678 / uSize;
  float f = pow(pow(abs(q.x), 4.0) + pow(abs(q.y), 4.0), 0.25);
  float d = (f - 1.0) * uSize;
  float core = exp(-pow(d / (0.035 * uSize), 2.0));
  float glow = exp(-pow(d / (0.16 * uSize), 2.0));
  float inside = (1.0 - smoothstep(0.92, 1.0, f)) * 0.07;
  // блик бежит по рамке по часовой стрелке
  float ang = atan(q.y, q.x);
  float run = pow(0.5 + 0.5 * cos(ang - uTime * 1.6), 10.0);
  float a = core * (0.75 + 0.6 * run) + glow * (0.32 + 0.4 * run) + inside;
  vec3 col = mix(vec3(0.18, 0.55, 1.0), vec3(0.75, 0.95, 1.0), core);
  outColor = vec4(col * a, a);
}`;
// Щит: стеклянные пластины по рамке (вертикальные, светлые края, к верху тают)
const PANEL_VS =
  COMMON +
  /* glsl */ `
in vec2 aCorner;
uniform vec3 uP0;
uniform vec3 uP1;
uniform vec2 uH;
out vec2 vUV;
void main() {
  vec3 p = mix(uP0, uP1, aCorner.x) + vec3(0.0, 0.0, mix(uH.x, uH.y, aCorner.x) * aCorner.y);
  vUV = aCorner;
  gl_Position = uVP * vec4(p, 1.0);
}`;
const PANEL_FS =
  COMMON +
  /* glsl */ `
in vec2 vUV;
uniform float uFade;
out vec4 outColor;
void main() {
  float side = min(vUV.x, 1.0 - vUV.x);
  float edge = smoothstep(0.16, 0.0, side);
  float top = 1.0 - vUV.y;
  float bottom = smoothstep(0.25, 0.0, vUV.y);
  float shimmer = 0.85 + 0.15 * sin(vUV.y * 18.0 - uTime * 3.0 + vUV.x * 6.0);
  float a = ((0.16 + 0.75 * edge) * (0.3 + 0.7 * top) + 0.35 * bottom) * shimmer * uFade;
  vec3 col = mix(vec3(0.25, 0.62, 1.0), vec3(0.8, 0.96, 1.0), edge);
  outColor = vec4(col * a, a);
}`;

// 3D-модель постройки: свои материалы (текстура или цвет), солнце, блеск металла, свечение, тени
const MODEL_VS =
  COMMON +
  /* glsl */ `
in vec3 aP;
in vec3 aN;
in vec2 aUV;
uniform mat4 uModel;
uniform float uShadowPass;
out vec3 vN;
out vec2 vUV;
out vec4 vLight;
out vec3 vPos;
void main() {
  vec4 p = uModel * vec4(aP, 1.0);
  vN = normalize(mat3(uModel) * aN);
  vUV = aUV;
  vPos = p.xyz;
  vLight = uSunVP * p;
  gl_Position = (uShadowPass > 0.5 ? uSunVP : uVP) * p;
}`;
const MODEL_FS =
  COMMON +
  SHADOW_FN +
  /* glsl */ `
uniform sampler2D uTex;
uniform float uUseTex;
uniform vec3 uColor;
uniform vec3 uMat; // металл, шероховатость, свечение
uniform float uShadowPass;
in vec3 vN;
in vec2 vUV;
in vec4 vLight;
in vec3 vPos;
out vec4 outColor;
void main() {
  if (uShadowPass > 0.5) { outColor = vec4(1.0); return; }
  vec3 alb = uUseTex > 0.5 ? pow(texture(uTex, vUV).rgb, vec3(2.2)) : uColor;
  vec3 n = normalize(vN);
  vec3 v = normalize(uEye - vPos);
  if (dot(n, v) < 0.0) n = -n;
  float lam = max(dot(n, uSunDir), 0.0);
  float sh = shadowAt(vLight);
  float gloss = mix(64.0, 6.0, uMat.y);
  float spec = pow(max(dot(n, normalize(uSunDir + v)), 0.0), gloss) * (0.15 + uMat.x) * sh;
  // металл светлее в рассеянном свете (отражает небо), иначе медь и золото выходят тёмными
  vec3 amb = uSkyCol * (0.7 + 0.5 * n.z) * (1.4 + uMat.x * 0.9);
  vec3 col = alb * (amb + uSunCol * lam * sh) + mix(vec3(1.0), alb, uMat.x) * uSunCol * spec * 0.8;
  col += alb * uMat.z * 1.6;
  outColor = vec4(toSrgb(fogged(col, vPos)), 1.0);
}`;

// ───────────────────────── утилиты GL ─────────────────────────
function compile(gl: WebGL2RenderingContext, vs: string, fs: string): WebGLProgram {
  const p = gl.createProgram()!;
  for (const [type, src] of [
    [gl.VERTEX_SHADER, vs],
    [gl.FRAGMENT_SHADER, fs],
  ] as const) {
    const s = gl.createShader(type)!;
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(`shader: ${gl.getShaderInfoLog(s)}`);
    gl.attachShader(p, s);
  }
  gl.linkProgram(p);
  if (!gl.getProgramParameter(p, gl.LINK_STATUS)) throw new Error(`link: ${gl.getProgramInfoLog(p)}`);
  return p;
}

type Uni = Record<string, WebGLUniformLocation | null>;
/** Первый номер вида-предмета (камни, трава, брёвна) — они идут после деревьев в списке видов. */
const PROP_NAMES = new Set(['tuft', 'pebbles', 'rock_a', 'rock_b', 'log', 'planks', 'stump']);
function propStartOf(variants: string[]): number {
  const k = variants.findIndex((v) => PROP_NAMES.has(v));
  return k < 0 ? variants.length : k;
}

function uniforms(gl: WebGL2RenderingContext, p: WebGLProgram): Uni {
  const out: Uni = {};
  const n = gl.getProgramParameter(p, gl.ACTIVE_UNIFORMS) as number;
  for (let i = 0; i < n; i++) {
    const info = gl.getActiveUniform(p, i)!;
    const name = info.name.replace(/\[0\]$/, '');
    out[name] = gl.getUniformLocation(p, info.name);
  }
  return out;
}

function toHalf(v: number): number {
  const f = new Float32Array([v]);
  const x = new Uint32Array(f.buffer)[0]!;
  const sign = (x >> 16) & 0x8000;
  const e = ((x >> 23) & 0xff) - 127 + 15;
  let m = x & 0x7fffff;
  if (e <= 0) return sign;
  if (e >= 31) return sign | 0x7c00;
  m >>= 13;
  return sign | (e << 10) | m;
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((res, rej) => {
    const im = new Image();
    im.decoding = 'async';
    im.onload = () => res(im);
    im.onerror = () => rej(new Error(`image ${url}`));
    im.src = url;
  });
}

function imageTexture(gl: WebGL2RenderingContext, im: TexImageSource, mip = true): WebGLTexture {
  const t = gl.createTexture()!;
  gl.bindTexture(gl.TEXTURE_2D, t);
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, im);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  if (mip) {
    gl.generateMipmap(gl.TEXTURE_2D);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
    const ext = gl.getExtension('EXT_texture_filter_anisotropic');
    if (ext) gl.texParameterf(gl.TEXTURE_2D, ext.TEXTURE_MAX_ANISOTROPY_EXT, 4);
  } else gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  return t;
}

function ortho(l: number, r: number, b: number, t: number, n: number, f: number): Float32Array {
  const m = new Float32Array(16);
  m[0] = 2 / (r - l);
  m[5] = 2 / (t - b);
  m[10] = -2 / (f - n);
  m[12] = -(r + l) / (r - l);
  m[13] = -(t + b) / (t - b);
  m[14] = -(f + n) / (f - n);
  m[15] = 1;
  return m;
}

function lookAtM(eye: number[], c: number[], up: number[]): Float32Array {
  let fx = c[0]! - eye[0]!,
    fy = c[1]! - eye[1]!,
    fz = c[2]! - eye[2]!;
  const fl = Math.hypot(fx, fy, fz);
  fx /= fl;
  fy /= fl;
  fz /= fl;
  let sx = fy * up[2]! - fz * up[1]!,
    sy = fz * up[0]! - fx * up[2]!,
    sz = fx * up[1]! - fy * up[0]!;
  const sl = Math.hypot(sx, sy, sz) || 1;
  sx /= sl;
  sy /= sl;
  sz /= sl;
  const ux = sy * fz - sz * fy,
    uy = sz * fx - sx * fz,
    uz = sx * fy - sy * fx;
  const m = new Float32Array(16);
  m[0] = sx;
  m[4] = sy;
  m[8] = sz;
  m[1] = ux;
  m[5] = uy;
  m[9] = uz;
  m[2] = -fx;
  m[6] = -fy;
  m[10] = -fz;
  m[12] = -(sx * eye[0]! + sy * eye[1]! + sz * eye[2]!);
  m[13] = -(ux * eye[0]! + uy * eye[1]! + uz * eye[2]!);
  m[14] = fx * eye[0]! + fy * eye[1]! + fz * eye[2]!;
  m[15] = 1;
  return m;
}

// ───────────────────────── загрузка и рендер ─────────────────────────
// ───── данные карты в памяти: грузятся и распаковываются ОДИН раз за игру (при входе в игру —
// preloadGround), повторный вход на карту не качает и не декодирует их заново ─────
const once = <T>(f: () => Promise<T>) => {
  let p: Promise<T> | null = null;
  return () => {
    if (!p)
      p = f().catch((e) => {
        p = null; // сбой сети — в следующий раз попробуем снова
        throw e;
      });
    return p;
  };
};
/** Картинка, уже распакованная в память (без задержки при первой отрисовке). */
async function decodedImage(url: string): Promise<HTMLImageElement> {
  const im = await loadImage(url);
  try {
    await im.decode();
  } catch {
    /* уже загружена — распакуется при отрисовке */
  }
  return im;
}
const baseData = once(async () => {
  const [manifest, heightBuf, overview] = await Promise.all([
    fetch(`${BASE}/manifest.json${VQ}`).then((r) => r.json() as Promise<Manifest>),
    fetch(`${BASE}/height.bin${VQ}`).then((r) => r.arrayBuffer()),
    decodedImage(`${BASE}/albedo/overview.webp${VQ}`),
  ]);
  return { manifest, heightBuf, overview };
});
const treesData = once(async () => {
  const [atlas, buf, albIm, nrmIm] = await Promise.all([
    fetch(`${BASE}/trees/atlas.json${VQ}`).then((r) => r.json() as Promise<Atlas>),
    fetch(`${BASE}/trees.bin${VQ}`).then((r) => r.arrayBuffer()),
    decodedImage(`${BASE}/trees/trees_albedo.webp${VQ}`),
    decodedImage(`${BASE}/trees/trees_normal.webp${VQ}`),
  ]);
  return { atlas, buf, albIm, nrmIm };
});
const bridgeData = once(async () => {
  const [data, im] = await Promise.all([
    fetch(`${BASE}/bridge/bridge.json${VQ}`).then(
      (r) => r.json() as Promise<{ pieces: Record<string, BridgePiece> }>,
    ),
    decodedImage(`${BASE}/bridge/bridge_albedo.webp${VQ}`),
  ]);
  return { data, im };
});
interface ModelPart {
  positions: number[];
  normals: number[];
  uvs: number[];
  indices: number[];
  tex: string | null;
  color: number[];
  metal: number;
  rough: number;
  emit: number;
  img?: HTMLImageElement;
}
const modelCache = new Map<string, Promise<{ size: number[]; parts: ModelPart[] }>>();
/** 3D-модель постройки: геометрия и текстуры — один раз за игру. */
function modelData(url: string) {
  let p = modelCache.get(url);
  if (!p) {
    p = (async () => {
      const m = (await fetch(`${url}/model.json${VQ}`).then((r) => r.json())) as {
        size: number[];
        parts: ModelPart[];
      };
      await Promise.all(
        m.parts.map(async (part) => {
          if (part.tex) part.img = await decodedImage(`${url}/${part.tex}${VQ}`);
        }),
      );
      return m;
    })();
    p.catch(() => modelCache.delete(url));
    modelCache.set(url, p);
  }
  return p;
}
/** Начать загрузку карты заранее (при входе в игру), чтобы она открывалась сразу. */
export function preloadGround(): void {
  void baseData()
    .then(({ manifest }) => prefetchTiles(manifest))
    .catch(() => undefined);
  void treesData().catch(() => undefined);
  void bridgeData().catch(() => undefined);
}
let tilesPrefetched = false;
/** Подробные плитки земли (≈5 МБ) — по одной в фоне в кэш телефона: приближение без подгрузки. */
function prefetchTiles(manifest: Manifest): void {
  if (tilesPrefetched) return;
  tilesPrefetched = true;
  const conn = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection;
  if (conn?.saveData) return;
  const queue = manifest.tiles.map(([i, j]) => `${BASE}/albedo/t_${i}_${j}.webp${VQ}`);
  const next = () => {
    const url = queue.shift();
    if (!url) return;
    // Через <img>: так запрос идёт как картинка и сервис-воркер кладёт её в кэш
    const im = new Image();
    im.onload = im.onerror = () => setTimeout(next, 150);
    im.src = url;
  };
  setTimeout(next, 4000);
}

export async function createGround(canvas: HTMLCanvasElement): Promise<WorldGround | null> {
  const glMaybe = canvas.getContext('webgl2', {
    antialias: true,
    alpha: false,
    powerPreference: 'high-performance',
  });
  if (!glMaybe) return null;
  const gl: WebGL2RenderingContext = glMaybe;
  const { manifest, heightBuf, overview: overviewIm } = await baseData();
  const HW = manifest.heightW;
  const HHh = manifest.heightH;
  const raw = new Uint16Array(heightBuf);
  const heights = new Float32Array(raw.length);
  for (let i = 0; i < raw.length; i++) heights[i] = (raw[i]! / 65535) * 16 - 6;
  const grid = archipelagoGrid(manifest.size);
  const terrainAt = (x: number, y: number): number => {
    const fx = Math.max(0, Math.min(HW - 1.001, x * 2));
    const fy = Math.max(0, Math.min(HHh - 1.001, y * 2));
    const ix = Math.floor(fx);
    const iy = Math.floor(fy);
    const tx = fx - ix;
    const ty = fy - iy;
    const i = iy * HW + ix;
    const h =
      (heights[i]! * (1 - tx) + heights[i + 1]! * tx) * (1 - ty) +
      (heights[i + HW]! * (1 - tx) + heights[i + HW + 1]! * tx) * ty;
    return h;
  };
  // Раскладка мостов (модули Codex: въезд 1.5, пролёт 2.0 клетки; настил на 0.9 выше основания)
  const END_LEN = 1.5;
  const SPAN_LEN = 2.0;
  const layouts = MAP_BRIDGES.map((b) => {
    const [x0, y0, x1, y1] = b as [number, number, number, number];
    const L = Math.hypot(x1 - x0, y1 - y0);
    const nspan = Math.max(1, Math.round((L - 2 * END_LEN) / SPAN_LEN));
    const total = 2 * END_LEN + nspan * SPAN_LEN;
    const kx = L / total;
    const z = Math.max(0, Math.min(terrainAt(x0, y0), terrainAt(x1, y1)) - 0.45);
    return { x0, y0, x1, y1, L, nspan, total, kx, z, ux: (x1 - x0) / L, uy: (y1 - y0) / L };
  });
  /** Высота настила моста в точке (или −∞, если точка не на мосту). */
  const deckAt = (x: number, y: number): number => {
    let best = Number.NEGATIVE_INFINITY;
    for (const b of layouts) {
      const dx = x - b.x0;
      const dy = y - b.y0;
      const along = dx * b.ux + dy * b.uy;
      const across = Math.abs(-dx * b.uy + dy * b.ux);
      if (along < -0.3 || along > b.L + 0.3 || across > BRIDGE_HALF + 0.35) continue;
      const ramp = END_LEN * b.kx;
      const t = Math.min(1, Math.max(0, Math.min(along, b.L - along) / ramp));
      best = Math.max(best, b.z + 0.45 + 0.45 * t);
    }
    return best;
  };
  // Войска идут по земле, а по мосту — по настилу
  const heightAt = (x: number, y: number): number => Math.max(terrainAt(x, y), deckAt(x, y), 0);

  // Текстура высот (R16F, линейная фильтрация доступна в WebGL2 без расширений)
  const half = new Uint16Array(heights.length);
  for (let i = 0; i < heights.length; i++) half[i] = toHalf(heights[i]!);
  const heightTex = gl.createTexture()!;
  gl.bindTexture(gl.TEXTURE_2D, heightTex);
  // Строка 1291 × 2 байта не кратна 4 — выравнивание по 1 байту
  gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.R16F, HW, HHh, 0, gl.RED, gl.HALF_FLOAT, half);
  gl.pixelStorei(gl.UNPACK_ALIGNMENT, 4);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

  const overview = imageTexture(gl, overviewIm);

  const progs = {
    terrain: compile(gl, TERRAIN_VS, TERRAIN_FS),
    depth: compile(gl, DEPTH_VS, DEPTH_FS),
    water: compile(gl, WATER_VS, WATER_FS),
    tree: compile(gl, TREE_VS, TREE_FS),
    grass: compile(gl, GRASS_VS, GRASS_FS),
    mesh: compile(gl, MESH_VS, MESH_FS),
    sprite: compile(gl, SPRITE_VS, SPRITE_FS),
    build: compile(gl, BUILD_VS, BUILD_FS),
    ring: compile(gl, RING_VS, RING_FS),
    model: compile(gl, MODEL_VS, MODEL_FS),
    panel: compile(gl, PANEL_VS, PANEL_FS),
  };
  const U = {
    terrain: uniforms(gl, progs.terrain),
    depth: uniforms(gl, progs.depth),
    water: uniforms(gl, progs.water),
    tree: uniforms(gl, progs.tree),
    grass: uniforms(gl, progs.grass),
    mesh: uniforms(gl, progs.mesh),
    sprite: uniforms(gl, progs.sprite),
    build: uniforms(gl, progs.build),
    ring: uniforms(gl, progs.ring),
    model: uniforms(gl, progs.model),
    panel: uniforms(gl, progs.panel),
  };

  // Сетка участка: (CHUNK·2+1)² вершин в половинах клетки
  const n = CHUNK * 2 + 1;
  const verts = new Float32Array(n * n * 2);
  for (let j = 0; j < n; j++)
    for (let i = 0; i < n; i++) {
      verts[(j * n + i) * 2] = i;
      verts[(j * n + i) * 2 + 1] = j;
    }
  const idx = new Uint32Array((n - 1) * (n - 1) * 6);
  let k = 0;
  for (let j = 0; j < n - 1; j++)
    for (let i = 0; i < n - 1; i++) {
      const a = j * n + i;
      idx[k++] = a;
      idx[k++] = a + n;
      idx[k++] = a + 1;
      idx[k++] = a + 1;
      idx[k++] = a + n;
      idx[k++] = a + n + 1;
    }
  const chunkVao = gl.createVertexArray()!;
  gl.bindVertexArray(chunkVao);
  const vb = gl.createBuffer()!;
  gl.bindBuffer(gl.ARRAY_BUFFER, vb);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  for (const p of [progs.terrain, progs.depth]) {
    const loc = gl.getAttribLocation(p, 'aPos');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
  }
  const ib = gl.createBuffer()!;
  gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ib);
  gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, idx, gl.STATIC_DRAW);
  const chunkCount = idx.length;
  gl.bindVertexArray(null);

  // Вода: один квадрат
  const quadVao = gl.createVertexArray()!;
  gl.bindVertexArray(quadVao);
  const qb = gl.createBuffer()!;
  gl.bindBuffer(gl.ARRAY_BUFFER, qb);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([0, 0, 1, 0, 0, 1, 1, 1]), gl.STATIC_DRAW);
  {
    const loc = gl.getAttribLocation(progs.water, 'aPos');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
  }
  gl.bindVertexArray(null);

  // Живая трава: плитка GRASS_TILE² клеток со случайными травинками, повторяется вокруг центра экрана
  const GRASS_TILE = 48;
  const coarse = typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;
  const grassCount = Math.round(GRASS_TILE * GRASS_TILE * (coarse ? 22 : 42));
  const grassVao = gl.createVertexArray()!;
  gl.bindVertexArray(grassVao);
  {
    const cb = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, cb);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, 0, 1, 0, -0.6, 0.5, 0.6, 0.5, 0, 1]),
      gl.STATIC_DRAW,
    );
    const lc = gl.getAttribLocation(progs.grass, 'aCorner');
    gl.enableVertexAttribArray(lc);
    gl.vertexAttribPointer(lc, 2, gl.FLOAT, false, 0, 0);
    let seed = 12345;
    // 32-битный LCG через Math.imul (обычное умножение теряет точность и зацикливается)
    const rnd = () => (seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0) / 4294967296;
    const inst = new Float32Array(grassCount * 4);
    for (let i = 0; i < grassCount; i++) {
      // пучки: травинки кучкуются вокруг случайных центров
      const cx = rnd() * GRASS_TILE;
      const cy = rnd() * GRASS_TILE;
      inst.set([cx + (rnd() - 0.5) * 0.08, cy + (rnd() - 0.5) * 0.08, rnd(), rnd()], i * 4);
      if (i % 4 !== 3 && i + 1 < grassCount) {
        for (let k = 1; k <= 2 && i + 1 < grassCount; k++) {
          i++;
          inst.set([cx + (rnd() - 0.5) * 0.08, cy + (rnd() - 0.5) * 0.08, rnd(), rnd()], i * 4);
        }
      }
    }
    const ib = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, ib);
    gl.bufferData(gl.ARRAY_BUFFER, inst, gl.STATIC_DRAW);
    const li = gl.getAttribLocation(progs.grass, 'aBlade');
    gl.enableVertexAttribArray(li);
    gl.vertexAttribPointer(li, 4, gl.FLOAT, false, 0, 0);
    gl.vertexAttribDivisor(li, 1);
  }
  gl.bindVertexArray(null);

  // Карта теней
  const shadowTex = gl.createTexture()!;
  gl.bindTexture(gl.TEXTURE_2D, shadowTex);
  gl.texStorage2D(gl.TEXTURE_2D, 1, gl.DEPTH_COMPONENT24, SHADOW, SHADOW);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_COMPARE_MODE, gl.COMPARE_REF_TO_TEXTURE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_COMPARE_FUNC, gl.LEQUAL);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  const shadowFbo = gl.createFramebuffer()!;
  gl.bindFramebuffer(gl.FRAMEBUFFER, shadowFbo);
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.TEXTURE_2D, shadowTex, 0);
  const shadowOk = gl.checkFramebufferStatus(gl.FRAMEBUFFER) === gl.FRAMEBUFFER_COMPLETE;
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);

  // Подробные плитки: грузятся по мере надобности, в памяти не больше TILE_CACHE
  const TILE_CACHE = 20;
  const tileSet = new Set(manifest.tiles.map(([i, j]) => `${i}_${j}`));
  const tiles = new Map<string, { tex: WebGLTexture | null; used: number; loading: boolean }>();
  let frameNo = 0;
  const hooks: { onLoaded: (() => void) | null } = { onLoaded: null };
  const tileFor = (i: number, j: number): WebGLTexture | null => {
    const key = `${i}_${j}`;
    if (!tileSet.has(key)) return null;
    let t = tiles.get(key);
    if (!t) {
      t = { tex: null, used: frameNo, loading: true };
      tiles.set(key, t);
      const entry = t;
      void loadImage(`${BASE}/albedo/t_${key}.webp${VQ}`)
        .then((im) => {
          entry.tex = imageTexture(gl, im);
          entry.loading = false;
          hooks.onLoaded?.();
        })
        .catch(() => {
          entry.loading = false;
        });
    }
    t.used = frameNo;
    return t.tex;
  };
  const evictTiles = () => {
    if (tiles.size <= TILE_CACHE) return;
    const list = [...tiles.entries()].filter(([, t]) => !t.loading).sort((a, b) => a[1].used - b[1].used);
    for (const [key, t] of list.slice(0, tiles.size - TILE_CACHE)) {
      if (t.used === frameNo) continue;
      if (t.tex) gl.deleteTexture(t.tex);
      tiles.delete(key);
    }
  };

  // ───── вырубка (для рельефа): 1 байт на клетку, 255 — лес вырублен ─────
  const clearedTex = gl.createTexture()!;
  gl.bindTexture(gl.TEXTURE_2D, clearedTex);
  gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.R8, 1, 1, 0, gl.RED, gl.UNSIGNED_BYTE, new Uint8Array([0]));
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  const uploadCleared = () => {
    const n = grid.size * grid.rows;
    const px = new Uint8Array(n);
    if (cleared) for (let i = 0; i < n; i++) px[i] = cleared[i]! & 1 ? 255 : 0;
    gl.bindTexture(gl.TEXTURE_2D, clearedTex);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.R8, grid.size, grid.rows, 0, gl.RED, gl.UNSIGNED_BYTE, px);
  };

  // ───── деревья ─────
  let trees: {
    vao: WebGLVertexArrayObject;
    buf: WebGLBuffer;
    data: Uint16Array;
    count: number;
    chunks: Map<string, [number, number]>;
    rects: Float32Array;
    dims: Float32Array;
    alb: WebGLTexture;
    nrm: WebGLTexture;
    sprUp: number[];
    sprToCam: number[];
    propStart: number;
    stump: number;
  } | null = null;
  let cleared: Uint8Array | null = null;
  const buildTreeBuffer = () => {
    if (!trees) return;
    const src = trees.data;
    const out = new Float32Array((src.length / 5) * 5);
    const chunks = new Map<string, [number, number]>();
    let m = 0;
    let lastKey = '';
    for (let i = 0; i < src.length; i += 5) {
      const x = src[i]! / 100;
      const y = src[i + 1]! / 100;
      const cx = Math.floor(x);
      const cy = Math.floor(y);
      let v = src[i + 4]!;
      let sc = src[i + 3]! / 1000;
      const cl =
        cleared && cx >= 0 && cy >= 0 && cx < grid.size && cy < grid.rows ? cleared[cy * grid.size + cx]! : 0;
      if (cl) {
        // У построек (бит 2) — ни деревьев, ни камней; в вырубленном лесу (бит 1) от части деревьев — пни
        if (cl & 2 || v >= trees.propStart) continue;
        if (trees.stump < 0 || (i * 2654435761) % 1000 > 330) continue;
        v = trees.stump;
        sc = 1.3 + ((i * 40503) % 300) / 1000;
      }
      const key = `${Math.floor(x / CHUNK)}_${Math.floor(y / CHUNK)}`;
      if (key !== lastKey) {
        const c = chunks.get(key);
        if (!c) chunks.set(key, [m, 0]);
        lastKey = key;
      }
      chunks.get(key)![1]++;
      out[m * 5] = x;
      out[m * 5 + 1] = y;
      out[m * 5 + 2] = src[i + 2]! / 1000 - 6;
      out[m * 5 + 3] = sc;
      out[m * 5 + 4] = v;
      m++;
    }
    gl.bindBuffer(gl.ARRAY_BUFFER, trees.buf);
    gl.bufferData(gl.ARRAY_BUFFER, out.subarray(0, m * 5), gl.STATIC_DRAW);
    trees.count = m;
    trees.chunks = chunks;
  };
  void (async () => {
    try {
      const { atlas, buf, albIm, nrmIm } = await treesData();
      const rects = new Float32Array(64);
      const dims = new Float32Array(64);
      manifest.treeVariants.forEach((name, v) => {
        const it = atlas.items[name];
        if (!it || v >= 16) return;
        rects.set([it.x / albIm.width, it.y / albIm.height, it.w / albIm.width, it.h / albIm.height], v * 4);
        dims.set([it.widthUnits, it.heightUnits, it.anchorX, it.anchorY], v * 4);
      });
      const vao = gl.createVertexArray()!;
      gl.bindVertexArray(vao);
      const cb = gl.createBuffer()!;
      gl.bindBuffer(gl.ARRAY_BUFFER, cb);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([0, 0, 1, 0, 0, 1, 1, 1]), gl.STATIC_DRAW);
      const lc = gl.getAttribLocation(progs.tree, 'aCorner');
      gl.enableVertexAttribArray(lc);
      gl.vertexAttribPointer(lc, 2, gl.FLOAT, false, 0, 0);
      const ibuf = gl.createBuffer()!;
      gl.bindVertexArray(null);
      const e = (atlas.camera_elevation_deg * Math.PI) / 180;
      trees = {
        vao,
        buf: ibuf,
        data: new Uint16Array(buf),
        count: 0,
        chunks: new Map(),
        rects,
        dims,
        alb: imageTexture(gl, albIm),
        nrm: imageTexture(gl, nrmIm),
        sprUp: [0, Math.sin(e), Math.cos(e)],
        sprToCam: [0, -Math.cos(e), Math.sin(e)],
        propStart: propStartOf(manifest.treeVariants),
        stump: manifest.treeVariants.indexOf('stump'),
      };
      buildTreeBuffer();
      hooks.onLoaded?.();
    } catch {
      trees = null;
    }
  })();
  const bindTreeInstances = (start: number) => {
    if (!trees) return;
    gl.bindBuffer(gl.ARRAY_BUFFER, trees.buf);
    const li = gl.getAttribLocation(progs.tree, 'aInst');
    const lv = gl.getAttribLocation(progs.tree, 'aVar');
    gl.enableVertexAttribArray(li);
    gl.vertexAttribPointer(li, 4, gl.FLOAT, false, 20, start * 20);
    gl.vertexAttribDivisor(li, 1);
    gl.enableVertexAttribArray(lv);
    gl.vertexAttribPointer(lv, 1, gl.FLOAT, false, 20, start * 20 + 16);
    gl.vertexAttribDivisor(lv, 1);
  };

  // ───── мосты ─────
  let bridges: { vao: WebGLVertexArrayObject; count: number; models: Float32Array[]; tex: WebGLTexture }[] =
    [];
  void (async () => {
    try {
      const { data, im } = await bridgeData();
      const tex = imageTexture(gl, im);
      const make = (p: BridgePiece) => {
        const vao = gl.createVertexArray()!;
        gl.bindVertexArray(vao);
        const put = (name: string, arr: number[], size: number) => {
          const b = gl.createBuffer()!;
          gl.bindBuffer(gl.ARRAY_BUFFER, b);
          gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(arr), gl.STATIC_DRAW);
          const loc = gl.getAttribLocation(progs.mesh, name);
          if (loc < 0) return;
          gl.enableVertexAttribArray(loc);
          gl.vertexAttribPointer(loc, size, gl.FLOAT, false, 0, 0);
        };
        put('aP', p.positions, 3);
        put('aN', p.normals, 3);
        put('aUV', p.uvs, 2);
        const b = gl.createBuffer()!;
        gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, b);
        gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint32Array(p.indices), gl.STATIC_DRAW);
        gl.bindVertexArray(null);
        // Границы по X (модули отцентрованы или начинаются в 0 — берём фактические)
        let minX = Number.POSITIVE_INFINITY;
        let maxX = Number.NEGATIVE_INFINITY;
        for (let i = 0; i < p.positions.length; i += 3) {
          minX = Math.min(minX, p.positions[i]!);
          maxX = Math.max(maxX, p.positions[i]!);
        }
        return { vao, count: p.indices.length, minX, len: maxX - minX };
      };
      const span = data.pieces.bridge_span;
      const end = data.pieces.bridge_end;
      if (!span || !end) return;
      const S = make(span);
      const E = make(end);
      const spanM: Float32Array[] = [];
      const endM: Float32Array[] = [];
      for (const lay of layouts) {
        const { x0, y0, x1, y1, nspan, total, kx, z } = lay;
        const ang = Math.atan2(-(y1 - y0), x1 - x0);
        const place = (piece: { minX: number; len: number }, off: number, flip: boolean) => {
          // Модель: сдвиг начала модуля в 0, растяжение по длине, поворот вдоль моста, перенос
          const c = Math.cos(ang);
          const s = Math.sin(ang);
          const m = new Float32Array(16);
          const fx = flip ? -1 : 1;
          // локальная ось X модуля → вдоль моста (с учётом разворота конца)
          m[0] = c * kx * fx;
          m[1] = s * kx * fx;
          m[4] = -s * fx * BRIDGE_WIDEN;
          m[5] = c * fx * BRIDGE_WIDEN;
          m[10] = 1;
          // центр модуля по X: off + len/2 (в единицах моста), сдвиг собственного minX
          const localC = flip ? off + piece.len + piece.minX : off - piece.minX;
          m[12] = x0 + c * kx * localC;
          m[13] = -y0 + s * kx * localC;
          m[14] = z;
          m[15] = 1;
          return m;
        };
        endM.push(place(E, 0, false));
        for (let i = 0; i < nspan; i++) spanM.push(place(S, E.len + i * S.len, false));
        endM.push(place(E, total - E.len, true));
      }
      bridges = [
        { vao: S.vao, count: S.count, models: spanM, tex },
        { vao: E.vao, count: E.count, models: endM, tex },
      ];
      hooks.onLoaded?.();
    } catch {
      bridges = [];
    }
  })();

  let gridA = 0;
  const clearA = new Float32Array(32);
  // Картинки в сцене (отряды в лесу): своя текстура на каждую, обновляется каждый кадр
  const spriteTex = new Map<string, { tex: WebGLTexture; used: number }>();
  const spriteVao = gl.createVertexArray()!;
  gl.bindVertexArray(spriteVao);
  {
    const b = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, b);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([0, 0, 1, 0, 0, 1, 1, 1]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(progs.sprite, 'aCorner');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
  }
  gl.bindVertexArray(null);
  // ───── постройки: картинки — текстуры один раз (с mip), щит на земле с глубиной и тенью ─────
  const buildVao = gl.createVertexArray()!;
  gl.bindVertexArray(buildVao);
  {
    const b = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, b);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([0, 0, 1, 0, 0, 1, 1, 1]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(progs.build, 'aCorner');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
  }
  gl.bindVertexArray(null);
  const buildTex = new Map<string, { tex: WebGLTexture; used: number }>();
  // Щит: общий квад (0..1)² для рамки и пластин
  const shieldQuad = (prog: WebGLProgram) => {
    const vao = gl.createVertexArray()!;
    gl.bindVertexArray(vao);
    const vb = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, vb);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([0, 0, 1, 0, 0, 1, 1, 1]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(prog, 'aCorner');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    gl.bindVertexArray(null);
    return vao;
  };
  // Рамка щита — сетка 24×24 (повторяет рельеф)
  const RING_N = 24;
  const ringVao = gl.createVertexArray()!;
  let ringCount = 0;
  {
    const v: number[] = [];
    const ix: number[] = [];
    for (let j = 0; j <= RING_N; j++) for (let i = 0; i <= RING_N; i++) v.push(i / RING_N, j / RING_N);
    for (let j = 0; j < RING_N; j++)
      for (let i = 0; i < RING_N; i++) {
        const a = j * (RING_N + 1) + i;
        ix.push(a, a + 1, a + RING_N + 1, a + 1, a + RING_N + 2, a + RING_N + 1);
      }
    gl.bindVertexArray(ringVao);
    const vb = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, vb);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(v), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(progs.ring, 'aCorner');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    const ib = gl.createBuffer()!;
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ib);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array(ix), gl.STATIC_DRAW);
    ringCount = ix.length;
    gl.bindVertexArray(null);
  }
  const panelVao = shieldQuad(progs.panel);
  /** Точка рамки щита (суперэллипс под 45°) по углу в осях ромба. */
  const ringPoint = (cx: number, cy: number, size: number, th: number): [number, number] => {
    const c = Math.cos(th);
    const s2 = Math.sin(th);
    const r = size / (c ** 4 + s2 ** 4) ** 0.25;
    const qa = c * r;
    const qb = s2 * r;
    return [cx + (qa + qb) * Math.SQRT1_2, cy + (qa - qb) * Math.SQRT1_2];
  };
  // Пластины: доля по рамке, ширина (доля размера), высоты левого и правого края (доли высоты)
  const PANELS = [
    [0.0, 0.42, 0.75, 1.0],
    [0.13, 0.3, 0.55, 0.42],
    [0.27, 0.48, 0.95, 0.7],
    [0.42, 0.34, 0.5, 0.62],
    [0.57, 0.44, 0.85, 1.05],
    [0.72, 0.3, 0.45, 0.38],
    [0.85, 0.4, 0.68, 0.9],
  ] as const;
  let shieldList: GroundShield[] = [];
  /** Щиты: задняя половина — до построек (её закрывает замок), передняя — после, поверх замка. */
  const drawShields = (view: View3D, light: Daylight, time: number, front: boolean) => {
    if (!shieldList.length) return;
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA); // свечение (цвет уже умножен на прозрачность)
    gl.depthMask(false);
    // рамка на земле
    gl.useProgram(progs.ring);
    setCommon(U.ring, view, light, time);
    gl.bindVertexArray(ringVao);
    const toCam = (sh: GroundShield) => {
      const dx = view.eye[0] - sh.x;
      const dy = view.eye[1] + sh.y;
      const l = Math.hypot(dx, dy) || 1;
      return [dx / l, dy / l] as const;
    };
    for (const sh of shieldList) {
      const z = heightAt(sh.x, sh.y);
      const [cx, cy] = toCam(sh);
      gl.uniform3f(U.ring.uSide!, cx, cy, front ? 1 : -1);
      gl.uniform3f(U.ring.uCenter!, sh.x, -sh.y, z);
      gl.uniform1f(U.ring.uSize!, sh.r);
      gl.drawElements(gl.TRIANGLES, ringCount, gl.UNSIGNED_SHORT, 0);
    }
    // пластины плывут по рамке по часовой стрелке (если смотреть сверху)
    gl.useProgram(progs.panel);
    setCommon(U.panel, view, light, time);
    gl.bindVertexArray(panelVao);
    const turn = time * 0.32;
    for (const sh of shieldList) {
      const z = heightAt(sh.x, sh.y);
      const [cx, cy] = toCam(sh);
      for (const [u, wd, h0, h1] of PANELS) {
        // оси ромба зеркальны осям карты: рост угла здесь = поворот по часовой стрелке на карте
        const th = (u * Math.PI * 2 + turn) % (Math.PI * 2);
        const half = (wd * Math.PI) / 2.2 / 2; // ширина — по дуге
        const [x0, y0] = ringPoint(sh.x, -sh.y, sh.r, th + half);
        const [x1, y1] = ringPoint(sh.x, -sh.y, sh.r, th - half);
        const isFront = ((x0 + x1) / 2 - sh.x) * cx + ((y0 + y1) / 2 + sh.y) * cy > 0;
        if (isFront !== front) continue;
        // низ пластины — по земле в своей точке (на склоне не уходит в землю и не висит)
        gl.uniform3f(U.panel.uP0!, x0, y0, heightAt(x0, -y0) + 0.02);
        gl.uniform3f(U.panel.uP1!, x1, y1, heightAt(x1, -y1) + 0.02);
        gl.uniform2f(U.panel.uH!, h0 * sh.h, h1 * sh.h);
        // пластина у дальнего края вспыхивает при подъёме, у ближнего — ровная
        gl.uniform1f(U.panel.uFade!, 0.85 + 0.15 * Math.sin(th * 3 + time));
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      }
    }
    gl.depthMask(true);
    gl.disable(gl.BLEND);
    gl.bindVertexArray(null);
  };
  let buildList: GroundBuilding[] = [];
  // ───── 3D-модели построек: данные — общий кэш (modelData), буферы и текстуры — на этот контекст ─────
  type GpuPart = {
    vao: WebGLVertexArrayObject;
    count: number;
    tex: WebGLTexture | null;
    color: number[];
    mat: number[];
  };
  const gpuModels = new Map<string, { parts: GpuPart[]; size: number[] } | 'loading'>();
  let modelList: GroundModel[] = [];
  const modelFor = (url: string) => {
    const hit = gpuModels.get(url);
    if (hit) return hit === 'loading' ? null : hit;
    gpuModels.set(url, 'loading');
    void modelData(url)
      .then((m) => {
        const parts: GpuPart[] = m.parts.map((p) => {
          const vao = gl.createVertexArray()!;
          gl.bindVertexArray(vao);
          const put = (name: string, arr: number[], n: number) => {
            const b = gl.createBuffer()!;
            gl.bindBuffer(gl.ARRAY_BUFFER, b);
            gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(arr), gl.STATIC_DRAW);
            const loc = gl.getAttribLocation(progs.model, name);
            if (loc < 0) return;
            gl.enableVertexAttribArray(loc);
            gl.vertexAttribPointer(loc, n, gl.FLOAT, false, 0, 0);
          };
          put('aP', p.positions, 3);
          put('aN', p.normals, 3);
          put('aUV', p.uvs, 2);
          const ib = gl.createBuffer()!;
          gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ib);
          gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint32Array(p.indices), gl.STATIC_DRAW);
          gl.bindVertexArray(null);
          let tex: WebGLTexture | null = null;
          if (p.img) {
            tex = imageTexture(gl, p.img);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.REPEAT);
          }
          return { vao, count: p.indices.length, tex, color: p.color, mat: [p.metal, p.rough, p.emit] };
        });
        gpuModels.set(url, { parts, size: m.size });
        hooks.onLoaded?.();
      })
      .catch(() => gpuModels.delete(url));
    return null;
  };
  const modelMatrix = (md: GroundModel, size: number[]) => {
    const k = md.w / Math.max(size[0]!, size[1]!);
    const c = Math.cos(md.rot) * k;
    const sn = Math.sin(md.rot) * k;
    // сидит основанием на самой низкой точке под собой (на склоне край уходит в землю, а не висит)
    const r = md.w * 0.45;
    let z = heightAt(md.x, md.y);
    for (const [dx, dy] of [
      [-r, -r],
      [r, -r],
      [-r, r],
      [r, r],
    ] as const)
      z = Math.min(z, heightAt(md.x + dx, md.y + dy));
    return new Float32Array([c, sn, 0, 0, -sn, c, 0, 0, 0, 0, k, 0, md.x, -md.y, z - 0.02, 1]);
  };
  const drawModels = (view: View3D, light: Daylight, time: number, shadowPass: boolean) => {
    if (!modelList.length) return;
    gl.useProgram(progs.model);
    setCommon(U.model, view, light, time, shadowPass);
    gl.uniform1f(U.model.uShadowPass!, shadowPass ? 1 : 0);
    gl.activeTexture(gl.TEXTURE6);
    gl.uniform1i(U.model.uTex!, 6);
    for (const md of modelList) {
      const m = modelFor(md.url);
      if (!m) continue;
      gl.uniformMatrix4fv(U.model.uModel!, false, modelMatrix(md, m.size));
      for (const p of m.parts) {
        if (!shadowPass) {
          gl.bindTexture(gl.TEXTURE_2D, p.tex);
          gl.uniform1f(U.model.uUseTex!, p.tex ? 1 : 0);
          gl.uniform3fv(U.model.uColor!, p.color);
          gl.uniform3fv(U.model.uMat!, p.mat);
        }
        gl.bindVertexArray(p.vao);
        gl.drawElements(gl.TRIANGLES, p.count, gl.UNSIGNED_INT, 0);
      }
    }
    gl.bindVertexArray(null);
  };
  /** Основание постройки — по самой высокой точке под ней (на склоне край не проваливается в землю). */
  const baseHeight = (b: GroundBuilding) => {
    const r = b.w * 0.4;
    let z = heightAt(b.x, b.y);
    for (const [dx, dy] of [
      [-r, 0],
      [r, 0],
      [0, -r * 0.5],
      [0, r * 0.5],
      [-r * 0.7, -r * 0.35],
      [r * 0.7, -r * 0.35],
    ] as const)
      z = Math.max(z, heightAt(b.x + dx, b.y + dy));
    return z;
  };
  const drawBuildings = (
    view: View3D,
    light: Daylight,
    time: number,
    shadowPass: boolean,
    sunRight?: number[],
  ) => {
    // Тени построек — силуэтом на земле (drawBuildingShadows), не через карту теней
    if (!buildList.length || shadowPass) return;
    gl.useProgram(progs.build);
    setCommon(U.build, view, light, time, shadowPass);
    gl.uniform1f(U.build.uShadowPass!, shadowPass ? 1 : 0);
    gl.uniform3fv(U.build.uRight!, shadowPass && sunRight ? sunRight : view.right);
    gl.uniform3fv(U.build.uUp!, shadowPass ? [0, 0, 1] : view.up);
    gl.bindVertexArray(buildVao);
    gl.activeTexture(gl.TEXTURE7);
    gl.uniform1i(U.build.uTex!, 7);
    // Ночью темнее, как и всё на карте
    const k = 0.45 + 0.55 * light.day;
    for (const b of buildList) {
      if (!b.img.complete || !b.img.naturalWidth) continue;
      let t = buildTex.get(b.key);
      if (!t) {
        t = { tex: imageTexture(gl, b.img), used: frameNo };
        buildTex.set(b.key, t);
      }
      t.used = frameNo;
      gl.bindTexture(gl.TEXTURE_2D, t.tex);
      const z = baseHeight(b);
      if (shadowPass) gl.uniform3f(U.build.uBase!, b.x, -b.y, z);
      else {
        // Ближе к камере (на полширины картинки): на склоне ни один край основания не уходит в землю
        const toEye = [view.eye[0] - b.x, view.eye[1] + b.y, view.eye[2] - z];
        const l = Math.hypot(toEye[0]!, toEye[1]!, toEye[2]!) || 1;
        // основание и так по самой высокой точке; сдвиг небольшой — передняя половина щита остаётся перед замком
        const lift = 0.25 + b.w * 0.12;
        gl.uniform3f(
          U.build.uBase!,
          b.x + (toEye[0]! / l) * lift,
          -b.y + (toEye[1]! / l) * lift,
          z + (toEye[2]! / l) * lift,
        );
        const dim = b.dim ?? 1;
        gl.uniform3f(
          U.build.uTint!,
          k * dim * (light.day > 0.5 ? 1 : 0.8),
          k * dim * (light.day > 0.5 ? 1 : 0.86),
          k * dim,
        );
        gl.uniform1f(U.build.uGray!, b.gray ?? 0);
      }
      gl.uniform4f(U.build.uRect!, b.w, b.h, b.ax, b.ay);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    }
    gl.bindVertexArray(null);
    if (!shadowPass)
      for (const [key, t] of buildTex)
        if (frameNo - t.used > 600) {
          gl.deleteTexture(t.tex);
          buildTex.delete(key);
        }
  };
  /** Тени построек: та же картинка, положенная на землю от солнца (длина — по высоте солнца). */
  const drawBuildingShadows = (view: View3D, light: Daylight, time: number) => {
    const sd = light.dir;
    if (!buildList.length || !shadowsOn || sd[2] <= 0.05) return;
    const hr = Math.hypot(sd[0], sd[1]) || 1;
    const len = Math.min(2.2, Math.max(0.35, hr / sd[2]));
    const strength = 0.42 * Math.min(1, light.day * 1.4) * Math.min(1, (sd[2] - 0.05) * 6);
    if (strength < 0.02) return;
    gl.useProgram(progs.build);
    setCommon(U.build, view, light, time);
    gl.uniform1f(U.build.uShadowPass!, 2);
    gl.uniform3f(U.build.uShadowDir!, (-sd[0] / hr) * len, (-sd[1] / hr) * len, 0);
    gl.uniform3f(U.build.uRight!, -sd[1] / hr, sd[0] / hr, 0);
    gl.uniform3f(U.build.uTint!, strength, 0, 0);
    gl.uniform1f(U.build.uGray!, 0);
    gl.bindVertexArray(buildVao);
    gl.activeTexture(gl.TEXTURE7);
    gl.uniform1i(U.build.uTex!, 7);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    gl.depthMask(false);
    for (const b of buildList) {
      const t = buildTex.get(b.key);
      if (!t) continue;
      gl.bindTexture(gl.TEXTURE_2D, t.tex);
      gl.uniform3f(U.build.uBase!, b.x, -b.y, baseHeight(b));
      // нижняя прозрачная кромка картинки не даёт тени: тень начинается у основания
      gl.uniform4f(U.build.uRect!, b.w * 0.92, b.h * 0.9, b.ax, b.ay);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    }
    gl.depthMask(true);
    gl.disable(gl.BLEND);
    gl.bindVertexArray(null);
  };
  const drawSprites = (view: View3D, light: Daylight, time: number, list: GroundSprite[]) => {
    if (!list.length) return;
    gl.useProgram(progs.sprite);
    setCommon(U.sprite, view, light, time);
    gl.uniform3fv(U.sprite.uRight!, view.right);
    gl.uniform3fv(U.sprite.uUp!, view.up);
    // Ночью отряд темнее, как и всё на карте
    const k = 0.45 + 0.55 * light.day;
    gl.uniform3f(U.sprite.uTint!, k * (light.day > 0.5 ? 1 : 0.8), k * (light.day > 0.5 ? 1 : 0.86), k);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    gl.depthMask(false);
    gl.bindVertexArray(spriteVao);
    gl.activeTexture(gl.TEXTURE7);
    gl.uniform1i(U.sprite.uTex!, 7);
    for (const sp of list) {
      let t = spriteTex.get(sp.key);
      if (!t) {
        const tex = gl.createTexture()!;
        gl.bindTexture(gl.TEXTURE_2D, tex);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        t = { tex, used: frameNo };
        spriteTex.set(sp.key, t);
      }
      t.used = frameNo;
      gl.bindTexture(gl.TEXTURE_2D, t.tex);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, sp.canvas);
      // Чуть ближе к камере, чтобы ноги не уходили в землю
      const z = heightAt(sp.x, sp.y) + 0.05;
      const toEye = [view.eye[0] - sp.x, view.eye[1] + sp.y, view.eye[2] - z];
      const l = Math.hypot(toEye[0]!, toEye[1]!, toEye[2]!) || 1;
      gl.uniform3f(
        U.sprite.uBase!,
        sp.x + (toEye[0]! / l) * 0.25,
        -sp.y + (toEye[1]! / l) * 0.25,
        z + (toEye[2]! / l) * 0.25,
      );
      gl.uniform4f(U.sprite.uRect!, sp.w, sp.h, sp.ax, sp.ay);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    }
    gl.depthMask(true);
    gl.disable(gl.BLEND);
    gl.bindVertexArray(null);
    for (const [key, t] of spriteTex)
      if (frameNo - t.used > 120) {
        gl.deleteTexture(t.tex);
        spriteTex.delete(key);
      }
  };

  let dpr = 1;
  let w = 1;
  let h = 1;
  let sunVP: Float32Array = new Float32Array(16);
  let shadowKey = '';
  let shadowsOn = true;
  let destroyed = false;

  const setCommon = (u: Uni, view: View3D, light: Daylight, time: number, shadowPass = false) => {
    gl.uniformMatrix4fv(u.uVP!, false, view.viewProj);
    gl.uniformMatrix4fv(u.uSunVP!, false, sunVP);
    gl.uniform2f(u.uHSize!, HW, HHh);
    gl.uniform2f(u.uWorld!, manifest.size, manifest.rows);
    gl.uniform3fv(u.uSunDir!, light.dir);
    gl.uniform3fv(u.uSunCol!, light.sun);
    gl.uniform3fv(u.uSkyCol!, light.sky);
    gl.uniform3fv(u.uEye!, view.eye);
    // Дымка вдали — заметна только при наклоне
    const near = view.dist * 1.1;
    gl.uniform4f(u.uFog!, near, near + view.dist * 2.5, 0.55 * Math.sin(view.pitch) + 0.05, 0);
    const fog = light.day > 0.5 ? [0.62, 0.74, 0.86] : [0.05, 0.07, 0.14];
    gl.uniform3f(u.uFogCol!, fog[0]! * (0.3 + light.day * 0.7), fog[1]! * (0.3 + light.day * 0.7), fog[2]!);
    gl.uniform1f(u.uTime!, time);
    if (u.uGrid) gl.uniform1f(u.uGrid, gridA);
    if (u.uClear) gl.uniform4fv(u.uClear, clearA);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, heightTex);
    gl.uniform1i(u.uHeight!, 0);
    if (u.uShadow) {
      gl.activeTexture(gl.TEXTURE1);
      // В проходе теней карта теней — цель рендера: читать её нельзя
      gl.bindTexture(gl.TEXTURE_2D, shadowPass ? null : shadowTex);
      gl.uniform1i(u.uShadow, 1);
      gl.uniform1f(u.uShadowOn!, shadowOk && !shadowPass && shadowsOn ? 1 : 0);
    }
  };

  const visibleChunks = (x0: number, y0: number, x1: number, y1: number) => {
    const out: [number, number][] = [];
    const cx0 = Math.max(0, Math.floor(x0 / CHUNK));
    const cy0 = Math.max(0, Math.floor(y0 / CHUNK));
    const cx1 = Math.min(Math.ceil(manifest.size / CHUNK) - 1, Math.floor(x1 / CHUNK));
    const cy1 = Math.min(Math.ceil(manifest.rows / CHUNK) - 1, Math.floor(y1 / CHUNK));
    for (let j = cy0; j <= cy1; j++) for (let i = cx0; i <= cx1; i++) out.push([i, j]);
    return out;
  };

  const ground: WorldGround = {
    get ready() {
      return true;
    },
    heightAt,
    setClearedCells(cells) {
      cleared = cells;
      buildTreeBuffer();
      uploadCleared();
    },
    resize(nw, nh, ndpr) {
      w = nw;
      h = nh;
      dpr = Math.min(ndpr, 2);
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
    },
    render(view, light, time, opts) {
      gridA = opts?.grid ?? 0;
      buildList = opts?.buildings ?? [];
      modelList = opts?.models ?? [];
      shieldList = opts?.shields ?? [];
      // Отряды внутри леса: деревья вплотную к ним расступаются (не больше 8)
      clearA.fill(0);
      (opts?.sprites ?? []).slice(0, 8).forEach((sp, k) => clearA.set([sp.x, sp.y, 0.55, 0], k * 4));
      const useShadows = shadowOk && opts?.shadows !== false;
      shadowsOn = useShadows;
      if (!useShadows) shadowKey = '';
      if (destroyed) return;
      frameNo++;
      // Видимая область земли (клетки) с запасом под горы
      const corners = [
        [0, 0],
        [w, 0],
        [0, h],
        [w, h],
      ].map(([sx, sy]) => {
        const nx = (sx! / w) * 2 - 1;
        const ny = 1 - (sy! / h) * 2;
        const m = view.inv;
        const tr = (z: number) => {
          const X = m[0]! * nx + m[4]! * ny + m[8]! * z + m[12]!;
          const Y = m[1]! * nx + m[5]! * ny + m[9]! * z + m[13]!;
          const Z = m[2]! * nx + m[6]! * ny + m[10]! * z + m[14]!;
          const W = m[3]! * nx + m[7]! * ny + m[11]! * z + m[15]!;
          return [X / W, Y / W, Z / W];
        };
        const a = tr(-1);
        const b = tr(1);
        const t = (0 - a[2]!) / (b[2]! - a[2]!);
        return [a[0]! + (b[0]! - a[0]!) * t, -(a[1]! + (b[1]! - a[1]!) * t)];
      });
      const x0 = Math.min(...corners.map((c) => c[0]!)) - 8;
      const x1 = Math.max(...corners.map((c) => c[0]!)) + 8;
      const y0 = Math.min(...corners.map((c) => c[1]!)) - 10;
      const y1 = Math.max(...corners.map((c) => c[1]!)) + 8;
      const chunks = visibleChunks(x0, y0, x1, y1);

      // ── тени: свет смотрит из-за видимой области по направлению солнца ──
      const sd = light.dir;
      const mkey = modelList.reduce(
        (a, m) =>
          (a * 31 + Math.round(m.x * 7 + m.y * 13) + (gpuModels.get(m.url) === 'loading' ? 0 : 1)) % 1e9,
        7,
      );
      const bkey =
        mkey +
        buildList.reduce(
          (a, b) => (a * 31 + Math.round(b.x * 7 + b.y * 13 + b.h * 100)) % 1e9,
          buildList.length,
        );
      const key = `${Math.round(x0 / 8)}:${Math.round(y0 / 8)}:${Math.round(x1 / 8)}:${Math.round(y1 / 8)}:${sd.map((v) => v.toFixed(3)).join(',')}:${trees?.count ?? 0}:${bridges.length}:${bkey}`;
      if (useShadows && key !== shadowKey) {
        shadowKey = key;
        const cx = (x0 + x1) / 2;
        const cy = -(y0 + y1) / 2;
        const eye = [cx + sd[0] * 300, cy + sd[1] * 300, sd[2] * 300];
        const up = Math.abs(sd[2]) > 0.95 ? [0, 1, 0] : [0, 0, 1];
        const lv = lookAtM(eye, [cx, cy, 0], up);
        // Границы: углы области на высотах −1…9 в осях света
        let l = Number.POSITIVE_INFINITY,
          r = Number.NEGATIVE_INFINITY,
          b = Number.POSITIVE_INFINITY,
          t = Number.NEGATIVE_INFINITY,
          nn = Number.POSITIVE_INFINITY,
          ff = Number.NEGATIVE_INFINITY;
        for (const X of [x0, x1])
          for (const Y of [y0, y1])
            for (const Z of [-1, 9]) {
              const px = lv[0]! * X + lv[4]! * -Y + lv[8]! * Z + lv[12]!;
              const py = lv[1]! * X + lv[5]! * -Y + lv[9]! * Z + lv[13]!;
              const pz = lv[2]! * X + lv[6]! * -Y + lv[10]! * Z + lv[14]!;
              l = Math.min(l, px);
              r = Math.max(r, px);
              b = Math.min(b, py);
              t = Math.max(t, py);
              nn = Math.min(nn, -pz);
              ff = Math.max(ff, -pz);
            }
        sunVP = mul4(ortho(l, r, b, t, nn - 5, ff + 5), lv);
        gl.bindFramebuffer(gl.FRAMEBUFFER, shadowFbo);
        gl.viewport(0, 0, SHADOW, SHADOW);
        gl.clear(gl.DEPTH_BUFFER_BIT);
        gl.enable(gl.DEPTH_TEST);
        gl.colorMask(false, false, false, false);
        gl.useProgram(progs.depth);
        setCommon(U.depth, view, light, time, true);
        gl.bindVertexArray(chunkVao);
        for (const [i, j] of chunks) {
          gl.uniform2f(U.depth.uChunk!, i * CHUNK, j * CHUNK);
          gl.drawElements(gl.TRIANGLES, chunkCount, gl.UNSIGNED_INT, 0);
        }
        if (trees) {
          gl.useProgram(progs.tree);
          setCommon(U.tree, view, light, time, true);
          gl.uniform1f(U.tree.uShadowPass!, 1);
          const hr = Math.hypot(sd[0], sd[1]) || 1;
          gl.uniform3f(U.tree.uSunRight!, -sd[1] / hr, sd[0] / hr, 0);
          drawTrees(chunks);
        }
        if (bridges.length) drawBridges(view, light, time, true);
        {
          const hr = Math.hypot(sd[0], sd[1]) || 1;
          drawBuildings(view, light, time, true, [-sd[1] / hr, sd[0] / hr, 0]);
          drawModels(view, light, time, true);
        }
        gl.colorMask(true, true, true, true);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      }

      // ── основной кадр ──
      gl.viewport(0, 0, canvas.width, canvas.height);
      const bg = light.day > 0.5 ? [0.05, 0.2, 0.32] : [0.01, 0.04, 0.1];
      gl.clearColor(bg[0]!, bg[1]!, bg[2]!, 1);
      gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
      gl.enable(gl.DEPTH_TEST);
      gl.enable(gl.CULL_FACE);
      gl.cullFace(gl.BACK);
      gl.useProgram(progs.terrain);
      setCommon(U.terrain, view, light, time);
      gl.activeTexture(gl.TEXTURE2);
      gl.bindTexture(gl.TEXTURE_2D, overview);
      gl.uniform1i(U.terrain.uOverview!, 2);
      gl.uniform1i(U.terrain.uDetail!, 3);
      gl.activeTexture(gl.TEXTURE8);
      gl.bindTexture(gl.TEXTURE_2D, clearedTex);
      gl.uniform1i(U.terrain.uCleared!, 8);
      gl.bindVertexArray(chunkVao);
      // Подробные плитки — только когда клетка на экране крупнее ~7 px
      const detail = view.ts > 7;
      for (const [i, j] of chunks) {
        const ti = Math.floor((i * CHUNK) / manifest.tile);
        const tj = Math.floor((j * CHUNK) / manifest.tile);
        const tex = detail ? tileFor(ti, tj) : null;
        gl.activeTexture(gl.TEXTURE3);
        gl.bindTexture(gl.TEXTURE_2D, tex ?? overview);
        gl.uniform4f(
          U.terrain.uDetailRect!,
          ti * manifest.tile,
          tj * manifest.tile,
          manifest.tile,
          tex ? 1 : 0,
        );
        gl.uniform2f(U.terrain.uChunk!, i * CHUNK, j * CHUNK);
        gl.drawElements(gl.TRIANGLES, chunkCount, gl.UNSIGNED_INT, 0);
      }
      gl.disable(gl.CULL_FACE);
      // Живая трава у камеры — только при приближении и с включёнными эффектами
      if (opts?.grass && view.ts > 16) {
        gl.useProgram(progs.grass);
        setCommon(U.grass, view, light, time);
        gl.activeTexture(gl.TEXTURE2);
        gl.bindTexture(gl.TEXTURE_2D, overview);
        gl.uniform1i(U.grass.uOverview!, 2);
        gl.uniform2f(U.grass.uCenter!, view.target[0], -view.target[1]);
        gl.uniform1f(U.grass.uTile!, GRASS_TILE);
        gl.uniform1f(U.grass.uPx!, 1 / view.ts);
        gl.uniform1f(
          U.grass.uRadius!,
          Math.min(GRASS_TILE / 2, (0.8 * Math.hypot(view.w, view.h)) / view.ts),
        );
        gl.bindVertexArray(grassVao);
        gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 5, grassCount);
        gl.bindVertexArray(null);
      }
      if (bridges.length) drawBridges(view, light, time, false);
      if (trees) {
        gl.useProgram(progs.tree);
        setCommon(U.tree, view, light, time);
        gl.uniform1f(U.tree.uShadowPass!, 0);
        gl.uniform3fv(U.tree.uRight!, view.right);
        gl.uniform3fv(U.tree.uUp!, view.up);
        drawTrees(chunks);
      }
      drawShields(view, light, time, false);
      drawModels(view, light, time, false);
      drawBuildings(view, light, time, false);
      drawBuildingShadows(view, light, time);
      drawSprites(view, light, time, opts?.sprites ?? []);
      drawShields(view, light, time, true);
      // Вода поверх дна (полупрозрачная), без записи глубины
      gl.useProgram(progs.water);
      setCommon(U.water, view, light, time);
      gl.uniform4f(
        U.water.uRect!,
        Math.max(-60, x0),
        Math.max(-60, y0),
        Math.min(manifest.size + 60, x1),
        Math.min(manifest.rows + 60, y1),
      );
      gl.enable(gl.BLEND);
      gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
      gl.depthMask(false);
      gl.bindVertexArray(quadVao);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      gl.depthMask(true);
      gl.disable(gl.BLEND);
      gl.bindVertexArray(null);
      evictTiles();
    },
    destroy() {
      destroyed = true;
      gl.getExtension('WEBGL_lose_context')?.loseContext();
    },
  };

  function drawTrees(chunks: [number, number][]) {
    if (!trees) return;
    gl.uniform4fv(U.tree.uRect!, trees.rects);
    gl.uniform4fv(U.tree.uDim!, trees.dims);
    gl.uniform1f(U.tree.uPropStart!, trees.propStart);
    gl.uniform3fv(U.tree.uSprUp!, trees.sprUp);
    gl.uniform3fv(U.tree.uSprToCam!, trees.sprToCam);
    gl.activeTexture(gl.TEXTURE4);
    gl.bindTexture(gl.TEXTURE_2D, trees.alb);
    gl.uniform1i(U.tree.uAlb!, 4);
    gl.activeTexture(gl.TEXTURE5);
    gl.bindTexture(gl.TEXTURE_2D, trees.nrm);
    gl.uniform1i(U.tree.uNrm!, 5);
    gl.bindVertexArray(trees.vao);
    for (const [i, j] of chunks) {
      const c = trees.chunks.get(`${i}_${j}`);
      if (!c || !c[1]) continue;
      bindTreeInstances(c[0]);
      gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, c[1]);
    }
    gl.bindVertexArray(null);
  }

  function drawBridges(view: View3D, light: Daylight, time: number, shadowPass: boolean) {
    gl.useProgram(progs.mesh);
    setCommon(U.mesh, view, light, time, shadowPass);
    gl.uniform1f(U.mesh.uShadowPass!, shadowPass ? 1 : 0);
    for (const b of bridges) {
      gl.activeTexture(gl.TEXTURE6);
      gl.bindTexture(gl.TEXTURE_2D, b.tex);
      gl.uniform1i(U.mesh.uTex!, 6);
      gl.bindVertexArray(b.vao);
      for (const m of b.models) {
        gl.uniformMatrix4fv(U.mesh.uModel!, false, m);
        gl.drawElements(gl.TRIANGLES, b.count, gl.UNSIGNED_INT, 0);
      }
    }
    gl.bindVertexArray(null);
  }

  // Новая плитка или деревья догрузились — перерисовать
  Object.defineProperty(ground, 'onLoaded', {
    set(fn: (() => void) | null) {
      hooks.onLoaded = fn;
    },
  });
  void invert4;
  return ground;
}

/** Подписка на догрузку ресурсов (перерисовка карты). */
export function onGroundLoaded(g: WorldGround, fn: () => void): void {
  (g as unknown as { onLoaded: () => void }).onLoaded = fn;
}
